/**
 * Nuclius Index methodology agents in running_order (matches backend JSON).
 * Progress keys are methodology agent_id / step_id — not legacy V7 B1–W letters.
 */

export type MethodologyPipelineStepDef = {
  id: string;
  /** Methodology agent_id (e.g. 1.7); null only for upload */
  agentId: string | null;
  label: string;
  disabled?: boolean;
  dynamic?: boolean;
};

/** Ordered agents: upload, then methodology running_order 1.1b … 3.4. */
export const METHODOLOGY_ANALYSIS_PIPELINE_STEPS: readonly MethodologyPipelineStepDef[] = [
  { id: 'upload', agentId: null, label: 'Upload script to server' },
  { id: '1.1b', agentId: '1.1b', label: '1.1b · Scene and event breakdown' },
  { id: '1.2', agentId: '1.2', label: '1.2 · Lead-character identification' },
  { id: '1.3', agentId: '1.3', label: '1.3 · Protagonist-centred beat analysis' },
  { id: '1.4', agentId: '1.4', label: '1.4 · Genre classification' },
  { id: '1.5', agentId: '1.5', label: '1.5 · Thematic analysis' },
  { id: '1.6', agentId: '1.6', label: '1.6 · Central dramatic problem' },
  { id: '1.7', agentId: '1.7', label: '1.7 · Central-problem dominance' },
  { id: '1.12', agentId: '1.12', label: '1.12 · Multi-strand narrative' },
  { id: '1.8', agentId: '1.8', label: '1.8 · Ten-stage structural beats' },
  { id: '1.9', agentId: '1.9', label: '1.9 · Qualifying beat analysis' },
  { id: '1.10', agentId: '1.10', label: '1.10 · Emotional-arc analysis' },
  { id: '1.11', agentId: '1.11', label: '1.11 · Transformation-beat analysis' },
  { id: '2.1', agentId: '2.1', label: '2.1 · Narrative force / pacing' },
  { id: '2.2', agentId: '2.2', label: '2.2 · Central-problem concentration' },
  { id: '2.3', agentId: '2.3', label: '2.3 · Scene effectiveness' },
  { id: '2.4', agentId: '2.4', label: '2.4 · Character-arc analysis' },
  { id: '2.5', agentId: '2.5', label: '2.5 · Dialogue analysis' },
  { id: '2.6', agentId: '2.6', label: '2.6 · Cultural specificity' },
  { id: '2.7', agentId: '2.7', label: '2.7 · Genre-expectation delivery' },
  { id: '3.1', agentId: '3.1', label: '3.1 · Multi-strand integration' },
  { id: '3.2', agentId: '3.2', label: '3.2 · Synopsis and logline' },
  { id: '3.3', agentId: '3.3', label: '3.3 · Originality assessment' },
  { id: '3.4', agentId: '3.4', label: '3.4 · Target audience' },
];

export const METHODOLOGY_AGENT_IDS: string[] = METHODOLOGY_ANALYSIS_PIPELINE_STEPS.filter(
  (s) => s.agentId != null
).map((s) => s.agentId as string);

// Named strands may include underscores/hyphens (e.g. will_ambition_distance:1.7.3)
const STRAND_STEP_RE = /^strand[_:]([A-Za-z0-9][A-Za-z0-9_-]*)[_:](.+)$/;
const STRAND_PREFIX_RE =
  /^([A-Za-z0-9][A-Za-z0-9_-]*):(\d+\.\d+[a-z]?(?:\.\d+)?)$/;

/** Parse strand-scoped progress ids (e.g. `A:1.7.3` or `will_ambition_distance:1.7.3`). */
export function parseStrandStepId(
  phaseId: string
): { strandId: string; stepId: string } | null {
  const m1 = STRAND_STEP_RE.exec(phaseId);
  if (m1) return { strandId: m1[1], stepId: m1[2] };
  const m2 = STRAND_PREFIX_RE.exec(phaseId);
  if (m2) return { strandId: m2[1], stepId: m2[2] };
  return null;
}

/** Compatibility alias: `phase` is the methodology step/agent id. */
export function parseStrandPhaseId(
  phaseId: string
): { strandId: string; phase: string } | null {
  const parsed = parseStrandStepId(phaseId);
  if (!parsed) return null;
  return { strandId: parsed.strandId, phase: parsed.stepId };
}

function agentIdFromStepId(stepId: string): string {
  // 1.1b.1 -> 1.1b ; 1.7.3 -> 1.7
  const m = /^(\d+\.\d+[a-z]?)/.exec(stepId);
  return m ? m[1] : stepId;
}

/** Human label for a methodology agent_id, step_id, or strand-scoped id. */
export function methodologyPhaseLabel(phaseId: string | null | undefined): string | null {
  if (!phaseId) return null;
  const strand = parseStrandStepId(phaseId);
  if (strand) {
    const agentId = agentIdFromStepId(strand.stepId);
    const base = METHODOLOGY_ANALYSIS_PIPELINE_STEPS.find((s) => s.id === agentId);
    const baseLabel = base?.label ?? strand.stepId;
    return `Strand ${strand.strandId} · ${baseLabel}`;
  }
  const agentId = agentIdFromStepId(phaseId);
  const step = METHODOLOGY_ANALYSIS_PIPELINE_STEPS.find(
    (s) => s.id === phaseId || s.id === agentId || s.agentId === phaseId
  );
  return step?.label ?? phaseId;
}
